/**
 * PieceGuardSettings — Settings › Piece Guard.
 *
 * The first time a `pcs` ingredient is bought, whoever is entering that bill is asked one
 * question: does it arrive IN PIECES or IN PACKETS (and how many to a packet)? That answer
 * is kept forever and decides how every later purchase of the item is read. This screen is
 * where the owner can see every answer that was given — and correct one that was wrong.
 *
 * ── WHAT CHANGING AN ANSWER DOES, AND DOES NOT DO ──────────────────────────
 * The answer itself feeds no calculation. What feeds the numbers — stock, stock value, cost
 * per piece, recipe and dish costs — is the piece COUNT each purchase was saved with, and a
 * wrong answer shows up there: "in packets" with "the 10 I entered was packets" saved 40
 * pieces; "in pieces" saved whatever packet count was typed as if it were pieces.
 *
 * So there are two steps, and the second is deliberately separate:
 *   1. Change the answer   — a new dated version (PUT /piece-guard/answers/:itemId). From
 *                            now on the stock-in form and the piece checks use it.
 *   2. Fix past purchases  — the purchases saved under the old answer, each with every
 *                            reading of its number priced side by side. The owner picks,
 *                            per purchase, what the number really was. The amount paid never
 *                            changes — only how many pieces it bought.
 *
 * ── HOW A PURCHASE IS CORRECTED ─────────────────────────────────────────────
 * Through the EXISTING line-item edit endpoint, one purchase at a time — the same door the
 * "Past pack mistakes" queue uses. That endpoint moves the batch and what is left of it,
 * the bill total, recipe costs and the report caches, and enforces the closing-count lock
 * and the admin PIN. Nothing is pre-selected and there is no one-tap bulk button: every
 * purchase is a separate human decision made against its own numbers, then read back once
 * more before anything is saved.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import http from '../services/httpClient';

const money = (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : `₹${Number(n).toFixed(2)}`);
const day = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? String(d).slice(0, 10)
    : t.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const qty = (n) => {
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : String(+v.toFixed(3));
};
const pcs = (n) => `${qty(n)} pc${Number(n) === 1 ? '' : 's'}`;

export const answerLabel = (arrivalMode, piecesPerPack) => (
  arrivalMode === 'single' || Number(piecesPerPack) === 1
    ? 'In pieces'
    : `In packets of ${piecesPerPack}`
);

const readError = (e, fallback) => e?.data?.error || e?.data?.message || e?.message || fallback;

// ════════════════════════════════════════════════════════════════════════════
// The list
// ════════════════════════════════════════════════════════════════════════════
const PieceGuardSettings = ({ businessId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [changing, setChanging] = useState(null);   // the answer row being changed
  const [fixing, setFixing] = useState(null);       // { item_id, item_name }
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/piece-guard/answers?businessId=${businessId}`);
      if (!res?.success) throw new Error(res?.error || 'Could not load the piece answers');
      setData(res);
    } catch (e) {
      setError(e?.data?.hint || readError(e, 'Could not load the piece answers'));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  // One card per ingredient: the business-wide answer, with any vendor's own answer under it.
  const items = useMemo(() => {
    const byItem = new Map();
    for (const a of data?.answers || []) {
      if (!byItem.has(a.item_id)) byItem.set(a.item_id, { itemLevel: null, vendors: [] });
      const entry = byItem.get(a.item_id);
      if (a.vendor_id == null) entry.itemLevel = a;
      else entry.vendors.push(a);
    }
    return [...byItem.values()]
      .map(e => ({ ...e, main: e.itemLevel || e.vendors[0] }))
      .filter(e => e.main);
  }, [data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(({ main }) => {
      if (q && !String(main.item_name || '').toLowerCase().includes(q)) return false;
      if (filter === 'single') return main.arrival_mode === 'single';
      if (filter === 'packet') return main.arrival_mode === 'packet';
      return true;
    });
  }, [items, search, filter]);

  const counts = useMemo(() => ({
    all: items.length,
    single: items.filter(e => e.main.arrival_mode === 'single').length,
    packet: items.filter(e => e.main.arrival_mode === 'packet').length,
  }), [items]);

  const canChange = data?.can_change === true;

  return (
    <div style={overlay} role="dialog" aria-label="Piece Guard answers">
      <div style={sheet}>
        <div style={sheetHeader}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Piece Guard</h2>
            <div style={{ ...muted, marginTop: 2 }}>Pieces or packets — the answer given for each pcs ingredient</div>
          </div>
          <button type="button" aria-label="Close" style={closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={sheetBody}>
          <div style={{ ...infoBox, marginBottom: 12 }}>
            The first time a <strong>pcs</strong> ingredient is bought, you are asked whether it
            arrives in pieces or in packets. Every answer is below. If one is wrong,
            {canChange ? ' change it, then fix the purchases that were saved with the wrong answer — ' : ' the owner can change it and fix the purchases saved with it — '}
            stock, cost per piece and recipe costs follow.
          </div>

          {notice && (
            <div role="status" style={{ ...infoBox, background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46', marginBottom: 12 }}>
              {notice}
            </div>
          )}

          <input
            type="search"
            placeholder="Search ingredient"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...input, width: '100%' }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {[['all', 'All'], ['single', 'In pieces'], ['packet', 'In packets']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                style={{ ...chip, ...(filter === key ? chipOn : {}) }}
              >
                {label} ({counts[key]})
              </button>
            ))}
          </div>

          {loading && <div style={{ ...muted, marginTop: 16 }}>Loading answers…</div>}
          {error && !loading && (
            <div style={{ ...infoBox, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginTop: 16 }}>
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {items.length === 0 && (
                <div style={{ ...muted, marginTop: 16 }}>
                  No pcs ingredient has been answered yet. The question is asked the first time
                  one is bought.
                </div>
              )}
              {items.length > 0 && visible.length === 0 && (
                <div style={{ ...muted, marginTop: 16 }}>No ingredient matches.</div>
              )}

              <div style={{ marginTop: 12 }}>
                {visible.map(({ main, itemLevel, vendors }) => (
                  <div key={main.item_id} style={card} data-testid={`piece-answer-${main.item_id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', wordBreak: 'break-word' }}>{main.item_name}</div>
                        <div style={{ ...muted, marginTop: 3 }}>
                          {Number(main.version) > 1 ? 'Changed' : 'Answered'} {day(main.effective_from)}
                          {main.answered_by_email ? ` by ${main.answered_by_email}` : ''}
                          {Number(main.version) > 1 && main.first_answered_at ? ` · first answered ${day(main.first_answered_at)}` : ''}
                        </div>
                        {!itemLevel && (
                          <div style={{ ...muted, marginTop: 2 }}>
                            Answer for {main.vendor_name || `vendor ${main.vendor_id}`} only
                          </div>
                        )}
                        {main.last_quantity != null && (
                          <div style={{ ...muted, marginTop: 2 }}>
                            Last purchase: {pcs(main.last_quantity)} at {money(main.last_unit_cost)} each
                          </div>
                        )}
                      </div>
                      <span style={main.arrival_mode === 'single' ? badgePieces : badgePackets}>
                        {answerLabel(main.arrival_mode, main.pieces_per_pack)}
                      </span>
                    </div>

                    {itemLevel && vendors.length > 0 && (
                      <div style={{ ...muted, marginTop: 6 }}>
                        {vendors.map(v => (
                          <div key={v.profile_id}>
                            From {v.vendor_name || `vendor ${v.vendor_id}`}: {answerLabel(v.arrival_mode, v.pieces_per_pack).toLowerCase()} (that vendor's own answer — not changed here)
                          </div>
                        ))}
                      </div>
                    )}

                    {canChange && itemLevel && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <button type="button" style={primaryBtn} onClick={() => setChanging(itemLevel)}>
                          Change answer
                        </button>
                        <button
                          type="button"
                          style={ghostBtn}
                          onClick={() => setFixing({ item_id: itemLevel.item_id, item_name: itemLevel.item_name })}
                        >
                          Fix past purchases
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {Number(data.unanswered_pcs_items) > 0 && (
                <div style={{ ...muted, marginTop: 12 }}>
                  {data.unanswered_pcs_items} other pcs ingredient{Number(data.unanswered_pcs_items) === 1 ? ' has' : 's have'} not
                  been asked yet — {Number(data.unanswered_pcs_items) === 1 ? 'it' : 'they'} will be asked the next time {Number(data.unanswered_pcs_items) === 1 ? 'it is' : 'they are'} bought.
                </div>
              )}
              {!canChange && items.length > 0 && (
                <div style={{ ...muted, marginTop: 12 }}>Only the owner can change an answer.</div>
              )}
            </>
          )}
        </div>
      </div>

      {changing && (
        <ChangeAnswerDialog
          businessId={businessId}
          row={changing}
          onCancel={() => setChanging(null)}
          onSaved={(res) => {
            const row = changing;
            setChanging(null);
            if (res?.unchanged) {
              setNotice(`${row.item_name} is already "${answerLabel(row.arrival_mode, row.pieces_per_pack).toLowerCase()}". Nothing changed.`);
              return;
            }
            setNotice(
              `${row.item_name} is now "${answerLabel(res.profile?.arrivalMode, res.profile?.piecesPerPack).toLowerCase()}". ` +
              'New purchases use this answer. Check the purchases saved with the old one below.'
            );
            load();
            // Step 2 follows straight away: an answer is only half corrected while the
            // purchases saved under the old one still carry its piece counts.
            setFixing({ item_id: row.item_id, item_name: row.item_name });
          }}
        />
      )}

      {fixing && (
        <FixPurchasesDialog
          businessId={businessId}
          item={fixing}
          onClose={() => setFixing(null)}
          onApplied={(n) => {
            if (n > 0) {
              setNotice(`${n} purchase${n === 1 ? '' : 's'} of ${fixing.item_name} corrected. Stock, cost per piece and recipe costs have been updated.`);
              load();
            }
          }}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Step 1 — change the answer
// ════════════════════════════════════════════════════════════════════════════
export const ChangeAnswerDialog = ({ businessId, row, onCancel, onSaved }) => {
  const [mode, setMode] = useState(row.arrival_mode === 'single' ? 'single' : 'packet');
  const [pack, setPack] = useState(row.arrival_mode === 'packet' ? String(row.pieces_per_pack) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const packNum = Number(pack);
  const packValid = Number.isInteger(packNum) && packNum >= 2 && packNum <= 500;
  const unchanged = mode === 'single'
    ? row.arrival_mode === 'single'
    : row.arrival_mode === 'packet' && packNum === Number(row.pieces_per_pack);
  const canSave = !saving && !unchanged && (mode === 'single' || packValid);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await http.put(`/piece-guard/answers/${row.item_id}?businessId=${businessId}`, {
        arrival_mode: mode,
        pieces_per_pack: mode === 'single' ? 1 : packNum,
      });
      if (!res?.success) throw new Error(res?.error || 'Could not save the answer');
      onSaved(res);
    } catch (e) {
      setError(readError(e, 'Could not save the answer'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...overlay, zIndex: 10003 }} role="dialog" aria-label={`Change answer for ${row.item_name}`}>
      <div style={dialog}>
        <h3 style={h3}>How does {row.item_name} arrive?</h3>
        <div style={muted}>
          Answered now: <strong>{answerLabel(row.arrival_mode, row.pieces_per_pack)}</strong>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          <button
            type="button"
            aria-pressed={mode === 'single'}
            style={{ ...choiceBtn, ...(mode === 'single' ? choiceOn : {}) }}
            onClick={() => setMode('single')}
          >
            <span style={{ fontSize: 15, fontWeight: 700 }}>In pieces</span>
            <span style={muted}>Counted one by one — 1 = 1 {row.item_name}</span>
          </button>
          <button
            type="button"
            aria-pressed={mode === 'packet'}
            style={{ ...choiceBtn, ...(mode === 'packet' ? choiceOn : {}) }}
            onClick={() => setMode('packet')}
          >
            <span style={{ fontSize: 15, fontWeight: 700 }}>In packets</span>
            <span style={muted}>Several pieces in one packet</span>
          </button>
        </div>

        {mode === 'packet' && (
          <div style={{ marginTop: 14 }}>
            <label style={label} htmlFor="piece-guard-pack">Pieces in one packet</label>
            <input
              id="piece-guard-pack"
              type="number"
              min="2"
              max="500"
              step="1"
              inputMode="numeric"
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              style={{ ...input, width: 120 }}
            />
            {pack !== '' && !packValid && (
              <div style={{ ...muted, color: '#b91c1c', marginTop: 4 }}>Enter a whole number from 2 to 500.</div>
            )}
          </div>
        )}

        <div style={{ ...infoBox, marginTop: 14 }}>
          Saving changes the answer for every new purchase. It does not change any purchase
          already saved — next you will see those, and choose for each one what its number
          really was.
        </div>

        {unchanged && <div style={{ ...muted, marginTop: 10 }}>That is already the answer.</div>}
        {error && <div style={{ ...muted, color: '#b91c1c', marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}
            disabled={!canSave}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save answer'}
          </button>
          <button type="button" style={ghostBtn} onClick={onCancel} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Step 2 — fix the purchases saved under the old answer
// ════════════════════════════════════════════════════════════════════════════
const KEEP = 'keep';

const basisNote = (l) => {
  if (l.basis?.source === 'recorded_correction') {
    return Number(l.basis.pack) === 1
      ? 'Confirmed as single pieces when it was corrected'
      : `Confirmed as packets of ${l.basis.pack} when it was entered`;
  }
  if (l.entered_under) {
    return `Saved while the answer was "${answerLabel(null, l.entered_under.pieces_per_pack).toLowerCase()}"`;
  }
  return 'Saved before this ingredient was first answered';
};

export const FixPurchasesDialog = ({ businessId, item, onClose, onApplied }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState({});      // line_item_id → option key | KEEP
  const [pin, setPin] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState({});    // line_item_id → { ok } | { error, needsAck }
  const [fixedCount, setFixedCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/piece-guard/answers/${item.item_id}/purchases?businessId=${businessId}`);
      if (!res?.success) throw new Error(res?.error || 'Could not load the purchases');
      setData(res);
      setChoice({});
    } catch (e) {
      setError(readError(e, 'Could not load the purchases'));
    } finally {
      setLoading(false);
    }
  }, [businessId, item.item_id]);

  useEffect(() => { load(); }, [load]);

  const lines = data?.lines || [];
  const chosen = lines
    .map(l => ({ line: l, option: (l.options || []).find(o => o.key === choice[l.line_item_id] && o.available) }))
    .filter(x => x.option);
  const pinNeeded = chosen.some(x => x.line.needs_pin);
  const blocked = data?.closing_count_in_progress === true;
  const canReview = chosen.length > 0 && !applying && !blocked && (!pinNeeded || pin.trim().length > 0);

  const answerText = data ? answerLabel(data.answer.arrival_mode, data.answer.pieces_per_pack) : '';

  /** One purchase through the existing edit door. Resolves to a result, never throws. */
  const correctOne = async ({ line, option }, { acknowledgeExtreme = false } = {}) => {
    try {
      const res = await http.put(`/stock-in/v2/${line.stock_in_id}/line-items/${line.line_item_id}`, {
        quantity: option.quantity,
        unit_cost: option.unit_cost,
        ...(pin.trim() ? { pin: pin.trim() } : {}),
        ...(acknowledgeExtreme ? { extreme_price_acknowledged: true } : {}),
        // Recorded as a pack correction, so the owner's weekly review shows it and a later
        // change of answer knows exactly how this count was reached.
        piece_confirmation: {
          index: 0,
          type: 'piece_pack_corrected',
          quantity: option.quantity,
          acknowledgment: `Settings › Piece Guard: answer is now "${answerText.toLowerCase()}" — ${qty(line.quantity)} → ${qty(option.quantity)} pcs`,
        },
      });
      // A 403 (the admin PIN) comes back as { success: false } rather than an exception.
      if (!res?.success) return { error: res?.error || 'Could not correct this purchase' };
      return { ok: true };
    } catch (e) {
      const requires = e?.data?.requires;
      return {
        error: readError(e, 'Could not correct this purchase'),
        needsAck: requires && requires.type === 'extreme_price_acknowledged',
      };
    }
  };

  const applyChosen = async () => {
    setApplying(true);
    const next = {};
    let ok = 0;
    // One at a time, in order: each edit refreshes stock and recipe costs for the business.
    for (const x of chosen) {
      const r = await correctOne(x);
      next[x.line.line_item_id] = r;
      if (r.ok) ok++;
    }
    setResults(next);
    setApplying(false);
    setReviewing(false);
    setFixedCount(n => n + ok);
    if (ok > 0) {
      onApplied && onApplied(ok);
      // Corrected purchases now match the answer and drop off the list; failures stay,
      // with their reason, and keep their choice.
      const failedChoices = Object.fromEntries(
        chosen.filter(x => !next[x.line.line_item_id].ok).map(x => [x.line.line_item_id, x.option.key])
      );
      await load();
      setChoice(failedChoices);
    }
  };

  const retryWithAck = async (line) => {
    const option = (line.options || []).find(o => o.key === choice[line.line_item_id]);
    if (!option) return;
    setApplying(true);
    const r = await correctOne({ line, option }, { acknowledgeExtreme: true });
    setResults(prev => ({ ...prev, [line.line_item_id]: r }));
    setApplying(false);
    if (r.ok) {
      setFixedCount(n => n + 1);
      onApplied && onApplied(1);
      await load();
    }
  };

  return (
    <div style={{ ...overlay, zIndex: 10002 }} role="dialog" aria-label={`Fix past purchases of ${item.item_name}`}>
      <div style={sheet}>
        <div style={sheetHeader}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827', wordBreak: 'break-word' }}>
              Fix past purchases — {item.item_name}
            </h2>
            {data && <div style={{ ...muted, marginTop: 2 }}>Answer now: <strong>{answerText}</strong></div>}
          </div>
          <button type="button" aria-label="Close" style={closeBtn} onClick={onClose} disabled={applying}>×</button>
        </div>

        <div style={sheetBody}>
          {loading && <div style={muted}>Loading purchases…</div>}
          {error && !loading && (
            <div style={{ ...infoBox, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{error}</div>
          )}

          {!loading && !error && data && (
            <>
              {fixedCount > 0 && (
                <div role="status" style={{ ...infoBox, background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46', marginBottom: 12 }}>
                  {fixedCount} purchase{fixedCount === 1 ? '' : 's'} corrected.
                </div>
              )}

              {blocked && (
                <div style={{ ...infoBox, background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412', marginBottom: 12 }}>
                  A closing stock count is in progress. Finish or park it before correcting purchases.
                </div>
              )}

              {lines.length === 0 ? (
                <div style={{ ...infoBox, marginBottom: 12 }}>
                  Nothing to fix. Every purchase of {item.item_name} since it was first answered
                  is already counted the way "{answerText.toLowerCase()}" says.
                </div>
              ) : (
                <div style={{ ...infoBox, marginBottom: 12 }}>
                  These purchases were saved with a different answer. For each one, choose what
                  its number really was — or keep it. <strong>The amount paid never changes</strong>;
                  only how many pieces it bought, and so the cost of one piece. Stock, stock value
                  and recipe costs update from the correction on; sales already recorded keep the
                  cost they were recorded at.
                </div>
              )}

              {lines.map((l) => {
                const result = results[l.line_item_id];
                const current = choice[l.line_item_id] || KEEP;
                return (
                  <div key={l.line_item_id} style={card} data-testid={`piece-fix-line-${l.line_item_id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                        {day(l.received_date)}{l.vendor_name ? ` · ${l.vendor_name}` : ''}
                      </div>
                      <div style={muted}>Bill #{l.stock_in_id}</div>
                    </div>
                    <div style={{ fontSize: 14, marginTop: 4, color: '#374151' }}>
                      Saved as <strong>{pcs(l.quantity)}</strong> at {money(l.unit_cost)} each · {money(l.line_total)} paid
                    </div>
                    <div style={{ ...muted, marginTop: 2 }}>{basisNote(l)}</div>
                    {l.needs_pin && (
                      <div style={{ ...muted, marginTop: 2, color: '#b45309' }}>
                        Sales were submitted after this bill — the admin PIN is needed to correct it.
                      </div>
                    )}

                    <div role="radiogroup" aria-label={`What was the ${qty(l.quantity)} on ${day(l.received_date)}?`} style={{ marginTop: 8 }}>
                      <label style={{ ...optionRow, ...(current === KEEP ? optionOn : {}) }}>
                        <input
                          type="radio"
                          name={`piece-fix-${l.line_item_id}`}
                          checked={current === KEEP}
                          onChange={() => setChoice(prev => ({ ...prev, [l.line_item_id]: KEEP }))}
                          disabled={applying}
                        />
                        <span>Keep as saved — {pcs(l.quantity)} at {money(l.unit_cost)} each</span>
                      </label>
                      {(l.options || []).map((o) => (
                        <label
                          key={o.key}
                          style={{ ...optionRow, ...(current === o.key ? optionOn : {}), ...(o.available ? {} : optionOff) }}
                        >
                          <input
                            type="radio"
                            name={`piece-fix-${l.line_item_id}`}
                            checked={current === o.key}
                            disabled={!o.available || applying}
                            onChange={() => setChoice(prev => ({ ...prev, [l.line_item_id]: o.key }))}
                          />
                          <span>
                            {o.reading}
                            <span style={optionResult}>→ {pcs(o.quantity)} at {money(o.price_per_piece)} each</span>
                            {!o.available && o.unavailable_reason && (
                              <span style={{ ...muted, display: 'block', color: '#b45309' }}>{o.unavailable_reason}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>

                    {result && result.error && (
                      <div style={{ ...muted, marginTop: 6, color: '#b91c1c' }}>
                        Not saved: {result.error}
                        {result.needsAck && (
                          <div style={{ marginTop: 6 }}>
                            <button type="button" style={ghostBtn} disabled={applying} onClick={() => retryWithAck(l)}>
                              I have checked — save it anyway
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {lines.length > 0 && (
                <div style={{ ...card, background: '#f9fafb' }}>
                  {pinNeeded && (
                    <div style={{ marginBottom: 10 }}>
                      <label style={label} htmlFor="piece-fix-pin">Admin PIN</label>
                      <input
                        id="piece-fix-pin"
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        style={{ ...input, width: 140 }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    style={{ ...primaryBtn, opacity: canReview ? 1 : 0.5, cursor: canReview ? 'pointer' : 'not-allowed' }}
                    disabled={!canReview}
                    onClick={() => setReviewing(true)}
                  >
                    {chosen.length === 0
                      ? 'Choose a correction above'
                      : `Review ${chosen.length} correction${chosen.length === 1 ? '' : 's'}`}
                  </button>
                </div>
              )}

              <div style={{ ...muted, marginTop: 10 }}>
                {Number(data.matched_count) > 0 && (
                  <div>{data.matched_count} other purchase{Number(data.matched_count) === 1 ? ' is' : 's are'} already counted the way this answer says.</div>
                )}
                {Number(data.other_vendor_count) > 0 && (
                  <div>{data.other_vendor_count} purchase{Number(data.other_vendor_count) === 1 ? ' is' : 's are'} from a vendor with its own answer and {Number(data.other_vendor_count) === 1 ? 'is' : 'are'} not listed.</div>
                )}
                {Number(data.older_count) > 0 && (
                  <div>{data.older_count} older purchase{Number(data.older_count) === 1 ? ' was' : 's were'} saved before this ingredient was first answered — the "Past pack mistakes" scan below the weekly review covers those.</div>
                )}
                {data.truncated && <div>Only the most recent purchases are shown.</div>}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Read back once more — nothing is saved until the person has seen every change. */}
      {reviewing && (
        <div style={{ ...overlay, zIndex: 10004 }} role="dialog" aria-label="Correct these purchases?">
          <div style={dialog}>
            <h3 style={h3}>Correct {chosen.length === 1 ? 'this purchase' : `these ${chosen.length} purchases`}?</h3>
            <div style={{ maxHeight: '45vh', overflowY: 'auto', marginTop: 8 }}>
              {chosen.map(({ line, option }) => (
                <div key={line.line_item_id} style={{ padding: '8px 0', borderTop: '1px solid #f3f4f6', fontSize: 14 }}>
                  <div style={{ fontWeight: 600 }}>{day(line.received_date)}{line.vendor_name ? ` · ${line.vendor_name}` : ''}</div>
                  <div style={{ color: '#374151' }}>
                    {pcs(line.quantity)} at {money(line.unit_cost)} → <strong>{pcs(option.quantity)} at {money(option.price_per_piece)}</strong>
                  </div>
                  <div style={muted}>{money(line.line_total)} paid — unchanged</div>
                </div>
              ))}
            </div>
            <div style={{ ...muted, marginTop: 10 }}>
              Each purchase is corrected on its bill. Stock, cost per piece and recipe costs
              update now; the Expense total does not change.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              <button type="button" style={primaryBtn} disabled={applying} onClick={applyChosen}>
                {applying ? 'Saving…' : `Yes — save ${chosen.length === 1 ? 'it' : `these ${chosen.length}`}`}
              </button>
              <button type="button" style={ghostBtn} disabled={applying} onClick={() => setReviewing(false)}>Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── styles, inline like the other piece screens so this drops in without a CSS build ──
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
};
const sheet = {
  background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '94vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  fontFamily: 'Roboto, sans-serif',
};
const sheetHeader = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb',
};
const sheetBody = { padding: '14px 18px 18px', overflowY: 'auto' };
const dialog = {
  background: '#fff', borderRadius: 12, padding: '20px 22px', width: '100%', maxWidth: 520,
  maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  fontFamily: 'Roboto, sans-serif',
};
const h3 = { margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827' };
const muted = { fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 };
const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const input = {
  padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const infoBox = {
  padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a',
  borderRadius: 10, fontSize: 13, lineHeight: 1.5,
};
const card = {
  border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff', marginBottom: 10,
};
const chip = {
  padding: '5px 11px', borderWidth: 1, borderStyle: 'solid', borderColor: '#d1d5db', borderRadius: 999, background: '#fff',
  fontSize: 12.5, color: '#374151', cursor: 'pointer',
};
const chipOn = { background: '#111827', borderColor: '#111827', color: '#fff' };
const badgeBase = { padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' };
const badgePieces = { ...badgeBase, background: '#f3f4f6', color: '#374151' };
const badgePackets = { ...badgeBase, background: '#dbeafe', color: '#1d4ed8' };
const primaryBtn = {
  padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
  fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
};
const ghostBtn = {
  padding: '7px 13px', background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 8, fontSize: 13.5, cursor: 'pointer',
};
const closeBtn = {
  border: 'none', background: 'transparent', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: '#6b7280',
};
const choiceBtn = {
  flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left',
  padding: '12px 14px', background: '#fff', color: '#111827', borderWidth: 1, borderStyle: 'solid', borderColor: '#cbd5e1',
  borderRadius: 10, cursor: 'pointer',
};
const choiceOn = { borderColor: '#2563eb', background: '#eff6ff', boxShadow: '0 0 0 1px #2563eb' };
const optionRow = {
  display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13.5, cursor: 'pointer',
  marginTop: 6, padding: '8px 10px', borderRadius: 8, borderWidth: 1, borderStyle: 'solid', borderColor: '#e5e7eb', background: '#fff',
};
const optionOn = { borderColor: '#2563eb', background: '#eff6ff' };
const optionOff = { cursor: 'not-allowed', opacity: 0.75 };
const optionResult = { display: 'block', marginTop: 2, fontSize: 13, fontWeight: 700, color: '#1d4ed8' };

export default PieceGuardSettings;
